import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { useOpenWindow } from '@/hooks';
import { getPaymentEntryUrl } from '@/shared/utils/paymentEntryUrl';
import { type PaymentEntry } from '@/shared/utils/paymentIntent';

export type PaymentSource = 'direct' | 'link' | 'redirect';

export type NavigationOptions = {
  /** 'new-tab' | 'same-tab' */
  target?: 'new-tab' | 'same-tab';
  /** 결제 진입 경로 */
  source?: PaymentSource;
  /** processing 재개 시 원래 미래 플랜 변경 대상 */
  targetQuizPlanHashId?: string;
};

type StartPaymentFlowPayload = PaymentEntry & { countryCode?: string };

/**
 * 결제 화면 이동 공통 헬퍼. `target` 이 'new-tab' 이면 새 창, 아니면 같은 탭에서 이동한다.
 *
 * 결제 진입(`usePaymentFlow`)과 checkout 안의 단계 이동(`useCheckoutNavigation`)이 함께 쓴다.
 */
export function usePaymentNavigate() {
  const router = useRouter();
  const openWindow = useOpenWindow();

  return useCallback(
    (url: string, options: NavigationOptions = {}) => {
      const { target = 'same-tab' } = options;

      switch (target) {
        case 'new-tab':
          openWindow(url);
          break;
        case 'same-tab':
        default:
          void router.push(url);
          break;
      }
    },
    [openWindow, router],
  );
}

/**
 * 결제 흐름 진입 훅 — 가격 페이지·업셀 모달에서 결제 방법 선택 페이지로 보낸다.
 *
 * 플로우 순서:
 * 1. pricing → checkout/method  ← 이 훅
 * 2. checkout/method → checkout (plan 선택 가능)
 * 3. checkout → checkout/payment?orderId=xxx
 * 4. checkout/payment → checkout/complete (성공 시)
 * 2~4 는 checkout 안의 이동이라 `pages/checkout/shared/hooks/useCheckoutNavigation` 이 맡는다.
 */
export function usePaymentFlow() {
  const navigate = usePaymentNavigate();

  // Step 1: 가격 페이지에서 결제 방법 선택 페이지로
  const startPaymentFlow = useCallback(
    (
      { countryCode, ...entry }: StartPaymentFlowPayload,
      options?: NavigationOptions,
    ) => {
      navigate(getPaymentEntryUrl(entry, countryCode), options);
    },
    [navigate],
  );

  return { startPaymentFlow };
}
